import type {
  Instructor,
  Service,
  ServiceCreate,
  ServiceRate,
  BookedServiceSessionModel,
  ServiceAvailability,
  DateSpecificAvailability,
  BookedServiceDetailModel,
  ParticipantBasicInfo,
  NewBookingSettings,
  AvailabilityData,
  AdminServiceCreatePostPayload,
  AdminServiceInstructorPayload,
  ServiceAvailabilityPayload,
  DateSpecificAvailabilityPayload,
  FacilityAsset,
} from "./types";
import type {
  AdminBookingSettings,
  AdminServiceAvailability,
  ServiceDocument,
  AdminServiceAvailabilityAvailabilityTypeEnum,
  AdminDateSpecificAvailability,
  AdminServiceAvailabilityDay,
  BookingSessionCoach,
  AdminFacilityAsset,
} from "@/api/model";
import { DayOfWeekEnum, ServiceTypeEnum } from "@/api/model";
import { formatTimeToHHMM, resolveMaxParticipantsForSave } from "./helpers";
import { formatApiTime } from "@/shared/utils/helpers";
import { useCurrentOrganization } from "@/composables/useCurrentOrganization";

import type {
  AdminServiceParticipantPaymentStatus,
  BookingCancellationSource,
} from "@/api/model";
import type { AdminService } from "@/api/model/adminService";
import type { AdminServiceInstructor } from "@/api/model/adminServiceInstructor";
import type { PaginatedAdminServiceInstructorList } from "@/api/model/paginatedAdminServiceInstructorList";
import type { ServiceRate as ApiServiceRateModel } from "@/api/model/serviceRate";
import type { AdminBookingSession } from "@/api/model/adminBookingSession";
import { determineSessionStatus, formatTimeRangeForDisplay } from "./helpers";
import type { AdminBookingSessionDetail } from "@/api/model/adminBookingSessionDetail";
import type { AdminServiceParticipant } from "@/api/model/adminServiceParticipant";
import { format, parseISO } from "date-fns";
import type { AdminServiceInstructor as ApiAdminServiceInstructor } from "@/api/model";
import type { InstructorFormData, PaginatedInstructorList } from "./types";
import {
  translateApiFacilityAssetAssignmentsToIds,
  translateFacilityAssetAssignmentsToApiPayload,
} from "./facility-asset-translator";
import type { ServiceUpdate } from "./types";
import type { AssetAssignmentEnum } from "@/api/model";

const translateServiceRate = (
  apiRate: ApiServiceRateModel,
  serviceId: string
): ServiceRate => {
  return {
    id: apiRate.id ?? "UNKNOWN_RATE_ID",
    serviceId: serviceId,
    durationMinutes: apiRate.duration_in_minutes ?? 0,
    amount: apiRate.amount ?? 0,
  };
};

const mapApiDayToFrontendType = (
  day: DayOfWeekEnum | null | undefined
): ServiceAvailability["dayOfWeekType"] => {
  switch (day) {
    case DayOfWeekEnum.SUNDAY:
      return "sunday";
    case DayOfWeekEnum.MONDAY:
      return "monday";
    case DayOfWeekEnum.TUESDAY:
      return "tuesday";
    case DayOfWeekEnum.WEDNESDAY:
      return "wednesday";
    case DayOfWeekEnum.THURSDAY:
      return "thursday";
    case DayOfWeekEnum.FRIDAY:
      return "friday";
    case DayOfWeekEnum.SATURDAY:
      return "saturday";
    default:
      return "everyday";
  }
};

// Map frontend dayOfWeekType string back to API DayOfWeekEnum
export const mapFrontendTypeToApiDay = (
  type: ServiceAvailability["dayOfWeekType"]
): DayOfWeekEnum | null => {
  switch (type) {
    case "sunday":
      return DayOfWeekEnum.SUNDAY;
    case "monday":
      return DayOfWeekEnum.MONDAY;
    case "tuesday":
      return DayOfWeekEnum.TUESDAY;
    case "wednesday":
      return DayOfWeekEnum.WEDNESDAY;
    case "thursday":
      return DayOfWeekEnum.THURSDAY;
    case "friday":
      return DayOfWeekEnum.FRIDAY;
    case "saturday":
      return DayOfWeekEnum.SATURDAY;
    default:
      return null;
  }
};

// Translate a single API ServiceAvailability to frontend ServiceAvailability
export const translateAdminServiceAvailability = (
  apiAvailability: AdminServiceAvailability
): ServiceAvailability => {
  const formatTime = (time: string | null | undefined) =>
    time ? time.substring(0, 5) : "00:00"; // Handle null/undefined

  let dayOfWeekType: ServiceAvailability["dayOfWeekType"];

  if (apiAvailability.availability_type) {
    const apiType = apiAvailability.availability_type.toLowerCase();
    if (["weekdays", "weekend", "everyday"].includes(apiType)) {
      dayOfWeekType = apiType as ServiceAvailability["dayOfWeekType"];
    } else {
      dayOfWeekType = mapApiDayToFrontendType(apiAvailability.day_of_week);
    }
  } else {
    dayOfWeekType = mapApiDayToFrontendType(apiAvailability.day_of_week);
  }

  return {
    id: apiAvailability.id,
    dayOfWeekType: dayOfWeekType,
    startTime: formatTime(apiAvailability.start_time),
    endTime: formatTime(apiAvailability.end_time),
  };
};

export const translateServiceAvailabilityToNewApiPayload = (
  feAvailability: ServiceAvailability
): ServiceAvailabilityPayload | null => {
  const hasStartTime = !!feAvailability.startTime;
  const hasEndTime = !!feAvailability.endTime;

  if (!hasStartTime && !hasEndTime) {
    return null;
  }

  if (!hasStartTime || !hasEndTime) {
    return null;
  }

  let availabilityType: string;
  let specificApiDay: DayOfWeekEnum | undefined;

  const dayType = feAvailability.dayOfWeekType;

  if (["everyday", "weekdays", "weekend"].includes(dayType)) {
    availabilityType = dayType.toUpperCase();
    specificApiDay = undefined;
  } else {
    const calculatedApiDay = mapFrontendTypeToApiDay(dayType);
    if (calculatedApiDay !== null) {
      availabilityType = "SINGLEDAY";
      specificApiDay = calculatedApiDay;
    } else {
      return null;
    }
  }

  const startTime = formatTimeToHHMM(feAvailability.startTime);
  const endTime = formatTimeToHHMM(feAvailability.endTime);

  const payload: ServiceAvailabilityPayload = {
    availability_type: availabilityType,
    ...(specificApiDay && { day_of_week: specificApiDay }),
    start_time: startTime,
    end_time: endTime,
  };

  return payload;
};

export const translateDateSpecificAvailabilityToApiPayload = (
  feAvailability: DateSpecificAvailability
): DateSpecificAvailabilityPayload | null => {
  if (feAvailability.id?.startsWith("temp-")) {
    console.warn(
      "Skipping temporary date-specific availability:",
      feAvailability.id
    );
    return null;
  }

  const startTimeTransformed = feAvailability.startTime
    ? formatTimeToHHMM(feAvailability.startTime)
    : null;
  const endTimeTransformed = feAvailability.endTime
    ? formatTimeToHHMM(feAvailability.endTime)
    : null;

  const payload: DateSpecificAvailabilityPayload = {
    availability_type: feAvailability.availabilityType,
    start_date: feAvailability.startDate,
    end_date: feAvailability.endDate,
    start_time: startTimeTransformed,
    end_time: endTimeTransformed,
  };

  return payload;
};

/**
 * Translates a single date-specific availability item from the API
 * to the frontend DateSpecificAvailability format.
 * @param apiDsa - The date-specific availability object from the API.
 *                 (Assumes AdminService has date_specific_availabilities: Array<ApiDateSpecificItemType>)
 *                 The exact type for apiDsa should be derived from your Orval-generated AdminService type.
 * @returns The translated DateSpecificAvailability object for the frontend.
 */
export const translateApiDateSpecificToFrontend = (
  apiDsa: AdminDateSpecificAvailability
): DateSpecificAvailability => {
  return {
    id: apiDsa.id,
    startDate: apiDsa.start_date,
    endDate: apiDsa.end_date,
    startTime: formatApiTime(apiDsa.start_time),
    endTime: formatApiTime(apiDsa.end_time),
    availabilityType: apiDsa.availability_type as "AVAILABLE" | "UNAVAILABLE",
  };
};

/**
 * Translates a single AdminService object from the API (Orval generated)
 * to the application's Service format.
 * @param apiService - The raw service object from the API (Orval generated).
 * @returns The translated Service object.
 */
export const translateService = (apiService: AdminService): Service => {
  return {
    id: apiService.id,
    title: apiService.title,
    description: apiService.description || "",
    serviceType: apiService.service_type ?? null,
    preBooked: apiService.pre_booked ?? false,
    instructorless: apiService.instructorless ?? false,
    asset_assignment:
      (apiService.asset_assignment?.toLowerCase() as "single" | "multiple") ??
      "single",
    isGroupService:
      !!apiService.max_participants && apiService.max_participants > 1,
    maxParticipants: apiService.max_participants ?? null,
    locationName: null,
    locationDescription: null,
    publishedAt: apiService.published_at,
    archivedAt: apiService.archived_at,
    instructorId: null,
    rates: apiService.service_rates
      ? apiService.service_rates.map((rate: ApiServiceRateModel) =>
          translateServiceRate(rate, apiService.id)
        )
      : [],
    weeklyAvailability: apiService.weekly_availability,
    serviceAvailabilities:
      apiService.service_availabilities?.map(
        translateAdminServiceAvailability
      ) ?? [],
    dateSpecificOverrides:
      apiService.date_specific_availabilities?.map(
        translateApiDateSpecificToFrontend
      ) ?? [],
    assignedInstructorIds:
      apiService.services_instructor_assignments?.map(
        (a) => a.services_instructor_id
      ) ?? [],
    assignedFacilityAssetIds: translateApiFacilityAssetAssignmentsToIds(
      apiService.facility_asset_assignments
    ),
    assignedInstructors: (() => {
      const instructors =
        apiService.services_instructor_assignments?.map((assignment) => {
          return {
            id: assignment.id,
            servicesInstructorId: assignment.services_instructor_id,
            name: assignment.instructor_name || "Unknown Instructor",
            email: (assignment as any).instructor_email || undefined,
            bio: (assignment as any).instructor_bio || undefined,
          };
        }) ?? [];
      return instructors;
    })(),
    assignedFacilityAssets: (() => {
      const assets =
        apiService.facility_asset_assignments?.map((assignment) => {
          return {
            id: assignment.id,
            facilityAssetId: assignment.facility_asset_id,
            name: assignment.asset_name || "Unknown Asset",
          };
        }) ?? [];
      return assets;
    })(),
    isBookable: apiService.is_bookable ?? null,
    isPublic: apiService.is_public ?? true,
    bookingLinkUrl: apiService.booking_link_url ?? null,
  };
};

/**
 * Translates an array of AdminService objects from the API
 * to the application's Service format.
 * @param apiServices - The array of raw service objects from the API.
 * @returns The array of translated Service objects.
 */
export const translateServiceList = (
  apiServices: AdminService[]
): Service[] => {
  return apiServices.map(translateService);
};

/**
 * Translates a frontend ServiceCreate object into the specific payload
 * required by the API for *creating* a new service (POST).
 * @param serviceCreate The frontend service creation data.
 * @returns The translated payload object for the API POST request.
 */
export const translateServiceCreateToApiAdminService = (
  serviceCreate: ServiceCreate
): AdminService => {
  const translatedAvailabilities = serviceCreate.serviceAvailabilities
    ?.map(translateServiceAvailabilityToNewApiPayload)
    .filter((avail): avail is ServiceAvailabilityPayload => avail !== null);

  const translatedDateSpecific = serviceCreate.dateSpecificOverrides
    ?.map(translateDateSpecificAvailabilityToApiPayload)
    .filter(
      (avail): avail is DateSpecificAvailabilityPayload => avail !== null
    );

  const instructorAssignments =
    serviceCreate.assignedInstructorIds?.map((id) => ({
      services_instructor_id: id,
    })) || [];

  const facilityAssetAssignments =
    translateFacilityAssetAssignmentsToApiPayload(
      serviceCreate.assignedFacilityAssetIds
    );

  const payload: AdminService = {
    title: serviceCreate.title,
    description: serviceCreate.description,
    service_type: serviceCreate.serviceType ?? null,
    pre_booked: serviceCreate.preBooked ?? false,
    max_participants: resolveMaxParticipantsForSave(serviceCreate),
    published: serviceCreate.published,
    asset_assignment:
      (serviceCreate.asset_assignment?.toUpperCase() as AssetAssignmentEnum) ??
      undefined,
    service_rates: serviceCreate.rates.map((rate) => ({
      duration_in_minutes: rate.durationMinutes,
      amount: rate.amount,
      id: undefined, // Dummy
      archived_at: null, // Dummy
      created_at: "", // Dummy
      updated_at: "", // Dummy
    })),
    service_availabilities: translatedAvailabilities
      ? translatedAvailabilities.map((item) => ({
          id: "",
          day_of_week: item.day_of_week,
          availability_type:
            item.availability_type as AdminServiceAvailabilityAvailabilityTypeEnum,
          start_time: item.start_time,
          end_time: item.end_time,
          created_at: "",
          updated_at: "",
        }))
      : [],
    date_specific_availabilities: translatedDateSpecific?.length
      ? translatedDateSpecific.map((item) => ({
          id: "",
          availability_type: item.availability_type,
          start_date: item.start_date,
          end_date: item.end_date,
          start_time: item.start_time ?? null,
          end_time: item.end_time ?? null,
          created_at: "",
          updated_at: "",
        }))
      : undefined,
    services_instructor_assignments: instructorAssignments.map((assign) => ({
      ...assign,
      id: "",
      created_at: "",
      updated_at: "",
      instructor_name: "", // Will be populated by server
      instructor_email: null,
      instructor_bio: null,
    })),
    facility_asset_assignments: facilityAssetAssignments.map((assign) => ({
      ...assign,
      id: "",
      created_at: "",
      updated_at: "",
      order: 0,
      asset_name: "",
    })),
    instructorless: serviceCreate.instructorless,
    is_public: serviceCreate.isPublic,
    // Dummy properties to satisfy AdminService type
    id: "",
    archived_at: null,
    created_at: "",
    updated_at: "",
    published_at: null,
    weekly_availability: [],
  };
  return payload;
};

/**
 * Translates a frontend Service object into the specific payload
 * required by the API for *updating* an existing service (PUT/PATCH).
 * @param service The frontend service data, including its ID and existing rate IDs.
 * @returns The translated payload object for the API PUT/PATCH request.
 */
export const translateServiceEditToApiAdminService = (
  service: Service
): AdminService => {
  const translatedAvailabilities = service.serviceAvailabilities
    ?.map(translateServiceAvailabilityToNewApiPayload)
    .filter((avail): avail is ServiceAvailabilityPayload => avail !== null);

  const translatedDateSpecific = service.dateSpecificOverrides
    ?.map(translateDateSpecificAvailabilityToApiPayload)
    .filter(
      (avail): avail is DateSpecificAvailabilityPayload => avail !== null
    );

  const instructorAssignments =
    service.assignedInstructorIds?.map((id) => ({
      services_instructor_id: id,
    })) || [];

  const facilityAssetAssignments =
    translateFacilityAssetAssignmentsToApiPayload(
      service.assignedFacilityAssetIds
    );

  const payload: AdminService = {
    title: service.title,
    description: service.description,
    service_type: service.serviceType ?? null,
    max_participants: resolveMaxParticipantsForSave(service),
    published: !!service.publishedAt && !service.archivedAt,
    asset_assignment:
      (service.asset_assignment?.toUpperCase() as AssetAssignmentEnum) ??
      undefined,
    service_rates: service.rates.map((rate) => {
      const ratePayload: {
        id?: string;
        duration_in_minutes: number;
        amount: number;
      } = {
        duration_in_minutes: rate.durationMinutes,
        amount: rate.amount,
      };
      if (rate.id && rate.id !== "UNKNOWN_RATE_ID") {
        ratePayload.id = rate.id;
      }

      return {
        id: ratePayload.id,
        duration_in_minutes: ratePayload.duration_in_minutes,
        amount: ratePayload.amount,
        archived_at: null,
        created_at: "",
        updated_at: "",
      };
    }),
    service_availabilities: translatedAvailabilities?.length
      ? translatedAvailabilities.map((item) => ({
          id: "",
          day_of_week: item.day_of_week === null ? undefined : item.day_of_week,
          availability_type:
            item.availability_type as AdminServiceAvailabilityAvailabilityTypeEnum,
          start_time: item.start_time,
          end_time: item.end_time,
          created_at: "",
          updated_at: "",
        }))
      : [],
    date_specific_availabilities: translatedDateSpecific?.length
      ? translatedDateSpecific.map((item) => ({
          id: "",
          availability_type: item.availability_type,
          start_date: item.start_date,
          end_date: item.end_date,
          start_time: item.start_time ?? null,
          end_time: item.end_time ?? null,
          created_at: "",
          updated_at: "",
        }))
      : undefined,
    services_instructor_assignments: instructorAssignments.map((assign) => ({
      ...assign,
      id: "",
      created_at: "",
      updated_at: "",
      instructor_name: "", // Will be populated by server
      instructor_email: null,
      instructor_bio: null,
    })),
    facility_asset_assignments: facilityAssetAssignments.map((assign) => ({
      ...assign,
      id: "",
      created_at: "",
      updated_at: "",
      order: 0,
      asset_name: "", // Will be populated by server
    })),
    id: service.id,
    archived_at: service.archivedAt ?? null,
    instructorless: service.instructorless,
    is_public: service.isPublic,
    created_at: "",
    updated_at: "",
    published_at: service.publishedAt,
    weekly_availability: service.weeklyAvailability
      ? service.weeklyAvailability.map((day) => day as DayOfWeekEnum)
      : [],
  };
  return payload;
};

export const translateFacilityAsset = (
  apiFacilityAsset: AdminFacilityAsset
): FacilityAsset => {
  return {
    id: apiFacilityAsset.id,
    name: apiFacilityAsset.name,
  };
};

export const translateFacilityAssetsList = (
  apiFacilityAssets: AdminFacilityAsset[]
): FacilityAsset[] => {
  return apiFacilityAssets.map(translateFacilityAsset);
};

export const translateInstructor = (
  apiInstructor: AdminServiceInstructor
): Instructor => {
  return {
    id: apiInstructor.id,
    coachId: apiInstructor.coach?.id as number,
    name: apiInstructor.coach?.profile?.name ?? "Unknown Name",
    email: apiInstructor.coach?.profile?.email ?? "",
    bio: apiInstructor.bio ?? "",
    serviceAvailabilities: apiInstructor.service_availabilities
      ? apiInstructor.service_availabilities.map(
          translateAdminServiceAvailability
        )
      : [],
    servicesCount: apiInstructor.services_count,
  };
};

export const translateInstructorList = (
  apiInstructors: PaginatedAdminServiceInstructorList
): PaginatedInstructorList => {
  return {
    count: apiInstructors.count,
    next: apiInstructors.next,
    previous: apiInstructors.previous,
    results: apiInstructors.results?.map(translateInstructor) ?? [],
  };
};

/**
 * Translates an AdminBookingSession (list item) to the application's BookedServiceSessionModel.
 * @param apiSession - The booking session data from the API list endpoint (Orval type).
 * @returns The translated BookedServiceSessionModel for application use.
 */
export const translateAdminBookingSessionToBookedServiceSessionModel = (
  apiSession: AdminBookingSession
): BookedServiceSessionModel => {
  const { currentOrganizationTimezone } = useCurrentOrganization();

  const instructorName = apiSession.instructor?.coach?.name ?? undefined;
  const serviceName = apiSession.service?.title ?? "Unknown Service";
  const maxParticipants = apiSession.service?.max_participants ?? null;
  const isClass = apiSession.service?.service_type === ServiceTypeEnum.class;
  const activeParticipants =
    apiSession.participants?.filter(
      (participant) => !participant.canceled_at
    ) ?? [];
  const participantCount = activeParticipants.length;
  const participantName =
    !isClass && participantCount === 1 ? activeParticipants[0].name : undefined;
  const facilityAssets =
    apiSession.facility_assets?.map((asset) => ({
      id: asset.id,
      name: asset.name,
    })) ?? [];

  const timeRange = formatTimeRangeForDisplay(
    apiSession.start_time,
    apiSession.end_time,
    currentOrganizationTimezone.value
  );

  const rawStatus = determineSessionStatus(
    apiSession.start_time,
    apiSession.end_time,
    apiSession.is_canceled ?? false
  );

  const status: BookedServiceSessionModel["status"] =
    rawStatus === "Active" ? "Upcoming" : rawStatus;

  return {
    bookedServiceId: apiSession.id,
    startTime: apiSession.start_time,
    timeRange: timeRange,
    serviceName: serviceName,
    serviceType: apiSession.service?.service_type,
    instructorName: instructorName,
    instructorId: apiSession.instructor?.id,
    serviceId: apiSession.service?.id,
    facilityAssets: facilityAssets,
    status: status,
    participantCount: participantCount,
    maxParticipants: maxParticipants,
    participantName: participantName,
    isClass: isClass,
  };
};

/**
 * Translates an AdminServiceParticipant from the API to ParticipantBasicInfo.
 */
export const translateAdminServiceParticipantToParticipantBasicInfo = (
  apiParticipant: AdminServiceParticipant
): ParticipantBasicInfo => {
  return {
    id: apiParticipant.id,
    bookingId: apiParticipant.booking_id ?? null,
    name: apiParticipant.name,
    email: apiParticipant.email,
    profileId: apiParticipant.profile_id ?? null,
    paymentId: apiParticipant.payment_id ?? null,
    phoneNumber: apiParticipant.phone_number ?? null,
    bookedByName: apiParticipant.booked_by?.name ?? null,
    bookedByEmail: apiParticipant.booked_by?.email ?? null,
    bookedByPhoneNumber: apiParticipant.booked_by?.phone_number ?? null,
    createdAt: apiParticipant.created_at,
    paymentStatus:
      apiParticipant.payment_status as AdminServiceParticipantPaymentStatus | null,
    paymentMethod: apiParticipant.payment_method_type ?? null,
    canceledAt: apiParticipant.canceled_at ?? null,
    canceledBy: apiParticipant.canceled_by as BookingCancellationSource | null,
    benefit_used: apiParticipant.benefit_used ?? null,
  };
};

/**
 * Translates an AdminBookingSessionDetail (API response) to the application's BookedServiceDetailModel.
 */
export const translateAdminBookingSessionDetailToBookedServiceDetailModel = (
  apiDetail: AdminBookingSessionDetail,
  orgTimezone: string
): BookedServiceDetailModel => {
  const startTime = apiDetail.start_time;
  const endTime = apiDetail.end_time;
  const isCanceled = apiDetail.is_canceled;

  const rawStatus = determineSessionStatus(startTime, endTime, isCanceled);
  const status: BookedServiceDetailModel["status"] =
    rawStatus === "Active" ? "Upcoming" : rawStatus;
  const displayTimeRange = formatTimeRangeForDisplay(
    startTime,
    endTime,
    orgTimezone
  );
  const date = format(parseISO(startTime), "yyyy-MM-dd");

  const participants = (apiDetail.participants ?? []).map(
    translateAdminServiceParticipantToParticipantBasicInfo
  );

  const facilityAssets =
    apiDetail.facility_assets?.map((asset) => ({
      id: asset.id,
      name: asset.name,
    })) ?? [];

  const maxParticipants = apiDetail.service?.max_participants ?? null;
  const isGroupService = !!maxParticipants && maxParticipants > 1;

  return {
    bookedServiceId: apiDetail.id,
    serviceId: apiDetail.service.id,
    serviceTitle: apiDetail.service.title,
    serviceDescription: apiDetail.service.description || null,
    serviceType: apiDetail.service.service_type ?? null,
    isGroupService: isGroupService,
    maxParticipants: maxParticipants,
    date: date,
    startTime: startTime,
    endTime: endTime,
    displayTimeRange: displayTimeRange,
    displayTimeZone: orgTimezone,
    status: status,
    rateId: apiDetail.rate.id ?? "UNKNOWN_RATE_ID",
    durationMinutes: apiDetail.rate.duration_in_minutes ?? 0,
    rateAmount: apiDetail.rate.amount ?? 0,
    instructorId: apiDetail.instructor?.id ?? null,
    instructorName: apiDetail.instructor?.coach?.name ?? null,
    instructors: (apiDetail.instructors ?? []).map((instructor) => ({
      id: instructor.id,
      name: instructor.coach.name,
    })),
    participants: participants,
    facilityAssets: facilityAssets,
  };
};

export const translateNewBookingSettingsToApiAdminBookingSettings = (
  newBookingSettings: NewBookingSettings
): Partial<AdminBookingSettings> => {
  const mappedServiceDocuments = (newBookingSettings.serviceDocuments || [])
    .filter((doc) => doc && (doc.asset_id || doc.asset_ticket_id))
    .map((doc) => ({
      ...(doc.asset_ticket_id
        ? { asset_ticket_id: doc.asset_ticket_id }
        : { asset_id: doc.asset_id }),
    }));

  return {
    facility_full_address: newBookingSettings.facilityFullAddress,
    booking_deadline_minutes: newBookingSettings.bookingDeadlineMinutes,
    refund_deadline_minutes: newBookingSettings.refundDeadlineMinutes,
    reschedule_deadline_minutes: newBookingSettings.rescheduleDeadlineMinutes,
    require_document_acceptance: newBookingSettings.requireDocumentAcceptance,
    service_documents: mappedServiceDocuments as ServiceDocument[],
  };
};

export const translateApiBookingSettingsToBookingSettingsModel = (
  apiSettings: AdminBookingSettings
): NewBookingSettings => {
  return {
    facilityFullAddress: apiSettings.facility_full_address ?? "",
    bookingDeadlineMinutes: apiSettings.booking_deadline_minutes ?? 0,
    refundDeadlineMinutes: apiSettings.refund_deadline_minutes ?? 0,
    rescheduleDeadlineMinutes: apiSettings.reschedule_deadline_minutes ?? 0,
    requireDocumentAcceptance: apiSettings.require_document_acceptance ?? false,
    serviceDocuments: (apiSettings.service_documents ?? []).map((doc) => ({
      asset_id: doc.asset.id,
      asset: doc.asset,
    })),
  };
};

/**
 * Translates API service availability data to the application's AvailabilityData format
 * @param apiAvailabilityDays - The availability data from the API
 * @returns Formatted availability data for the application
 */
export const translateApiAvailabilityToAvailabilityData = (
  apiAvailabilityDays: AdminServiceAvailabilityDay[]
): AvailabilityData[] => {
  return apiAvailabilityDays.map((day): AvailabilityData => {
    return {
      date: day.date,
      dayOfWeek: day.day_of_week,
      slots: Array.isArray(day.slots)
        ? day.slots.map((slot) => ({
            id: slot.id,
            date: slot.date,
            startTime: slot.start_time,
            endTime: slot.end_time,
            isAvailable: slot.is_available,
          }))
        : [],
    };
  });
};

export const translateAdminServiceInstructorToInstructorFormData = (
  apiInstructor: ApiAdminServiceInstructor
): InstructorFormData => {
  const coachIdFromApi = apiInstructor.coach?.id;

  if (typeof coachIdFromApi !== "number" && coachIdFromApi !== undefined) {
    console.error(
      "API returned non-number or missing coach.id for instructor (and coach.id was not undefined):",
      apiInstructor.id,
      "coach.id:",
      coachIdFromApi
    );
  } else if (coachIdFromApi === undefined) {
    console.error(
      "API returned missing coach or coach.id for instructor:",
      apiInstructor.id,
      "coach object:",
      apiInstructor.coach
    );
  }

  return {
    id: apiInstructor.id,
    coachId: typeof coachIdFromApi === "number" ? coachIdFromApi : null,
    coachName: apiInstructor.coach?.profile?.name ?? "Unknown Staff Member",
    bio: apiInstructor.bio ?? "",
    serviceAvailabilities: apiInstructor.service_availabilities
      ? apiInstructor.service_availabilities.map((avail) =>
          translateAdminServiceAvailability(avail)
        )
      : [],
    dateSpecificOverrides: apiInstructor.date_specific_availabilities
      ? apiInstructor.date_specific_availabilities.map((override) =>
          translateApiDateSpecificToFrontend(override)
        )
      : [],
    assignedServiceIds: apiInstructor.services_instructor_assignments
      ? apiInstructor.services_instructor_assignments.map(
          (assignment) => assignment.service_id
        )
      : [],
  };
};

export const translateInstructorFormDataToApiPayload = (
  formData: InstructorFormData
): AdminServiceInstructorPayload => {
  const serviceAvailabilitiesPayload = formData.serviceAvailabilities
    .map((sa) => translateServiceAvailabilityToNewApiPayload(sa))
    .filter((avail): avail is ServiceAvailabilityPayload => avail !== null);

  const dateSpecificAvailabilitiesPayload = formData.dateSpecificOverrides
    .map((dsa) => translateDateSpecificAvailabilityToApiPayload(dsa))
    .filter(
      (avail): avail is DateSpecificAvailabilityPayload => avail !== null
    );

  const servicesAssignmentsPayload = formData.assignedServiceIds.map(
    (serviceId) => ({
      service_id: serviceId,
    })
  );

  let coachIdAsNumber: number;
  if (typeof formData.coachId === "string") {
    coachIdAsNumber = parseInt(formData.coachId, 10);
  } else if (typeof formData.coachId === "number") {
    coachIdAsNumber = formData.coachId;
  } else {
    throw new Error(
      `Invalid or missing coachId in form data: "${formData.coachId}". Expected a number or a string parsable to a number.`
    );
  }

  if (isNaN(coachIdAsNumber)) {
    throw new Error(
      `Invalid coachId: "${formData.coachId}" cannot be converted to a number.`
    );
  }

  const payload: AdminServiceInstructorPayload = {
    bio: formData.bio,
    coach_id: coachIdAsNumber,
    services_instructor_assignments: servicesAssignmentsPayload,
    service_availabilities: serviceAvailabilitiesPayload,
    date_specific_availabilities: dateSpecificAvailabilitiesPayload,
  };

  return payload;
};

export const translateServiceUpdateToApiAdminService = (
  serviceUpdate: ServiceUpdate,
  baseService: Service
): AdminService => {
  const translatedAvailabilities = serviceUpdate.serviceAvailabilities
    ?.map(translateServiceAvailabilityToNewApiPayload)
    .filter((avail): avail is ServiceAvailabilityPayload => avail !== null);

  const translatedDateSpecific = serviceUpdate.dateSpecificOverrides
    ?.map(translateDateSpecificAvailabilityToApiPayload)
    .filter(
      (avail): avail is DateSpecificAvailabilityPayload => avail !== null
    );

  const instructorAssignments =
    serviceUpdate.assignedInstructorIds?.map((id) => ({
      services_instructor_id: id,
    })) || [];

  const facilityAssetAssignments =
    translateFacilityAssetAssignmentsToApiPayload(
      serviceUpdate.assignedFacilityAssetIds
    );

  const payload: AdminService = {
    title: serviceUpdate.title ?? baseService.title,
    description: serviceUpdate.description,
    service_type: serviceUpdate.serviceType ?? baseService.serviceType ?? null,
    pre_booked: serviceUpdate.preBooked ?? baseService.preBooked ?? false,
    max_participants: resolveMaxParticipantsForSave(serviceUpdate),
    published: serviceUpdate.published,
    instructorless: serviceUpdate.instructorless,
    asset_assignment:
      (serviceUpdate.asset_assignment?.toUpperCase() as AssetAssignmentEnum) ??
      undefined,
    service_rates: serviceUpdate.rates.map((rate) => ({
      id: rate.id && rate.id !== "UNKNOWN_RATE_ID" ? rate.id : undefined,
      duration_in_minutes: rate.durationMinutes,
      amount: rate.amount,
      archived_at: null, // Dummy
      created_at: "", // Dummy
      updated_at: "", // Dummy
    })),
    service_availabilities: translatedAvailabilities
      ? translatedAvailabilities.map((item) => ({
          id: "",
          day_of_week: item.day_of_week,
          availability_type:
            item.availability_type as AdminServiceAvailabilityAvailabilityTypeEnum,
          start_time: item.start_time,
          end_time: item.end_time,
          created_at: "",
          updated_at: "",
        }))
      : [],
    date_specific_availabilities:
      translatedDateSpecific !== undefined
        ? translatedDateSpecific.map((item) => ({
            id: "",
            availability_type: item.availability_type,
            start_date: item.start_date,
            end_date: item.end_date,
            start_time: item.start_time ?? null,
            end_time: item.end_time ?? null,
            created_at: "",
            updated_at: "",
          }))
        : baseService?.dateSpecificOverrides?.map((dso) => ({
            id: dso.id,
            availability_type: dso.availabilityType,
            start_date: dso.startDate,
            end_date: dso.endDate,
            start_time: dso.startTime ? formatTimeToHHMM(dso.startTime) : null,
            end_time: dso.endTime ? formatTimeToHHMM(dso.endTime) : null,
            created_at: "",
            updated_at: "",
          })) ?? [],
    services_instructor_assignments: instructorAssignments.map((assign) => ({
      ...assign,
      id: "",
      created_at: "",
      updated_at: "",
      instructor_name: "", // Will be populated by server
      instructor_email: null,
      instructor_bio: null,
    })),
    facility_asset_assignments: facilityAssetAssignments.map((assign) => ({
      ...assign,
      id: "",
      created_at: "",
      updated_at: "",
      order: 0,
      asset_name: "",
    })),
    is_public: serviceUpdate.isPublic ?? baseService.isPublic,
    // Inherited from base
    id: baseService.id,
    archived_at: baseService.archivedAt ?? null,
    created_at: "", // Dummy
    updated_at: "", // Dummy
    published_at: baseService.publishedAt,
    weekly_availability:
      baseService.weeklyAvailability?.map((day) => day as DayOfWeekEnum) ?? [],
  };
  return payload;
};
